import { useCachedActions } from '@hooks/useCachedActions';
import type { FetchData } from '@hooks/useDataFetcher';
import { buildCacheKey, toQueryStringValue } from '@shared/util/cache-utils';
import { useMemo } from 'react';
import type {
  IPcfCarConfigsResponse,
  IPcfMarketAuthEntriesResponse,
  IPcfMissingObject,
  IPcfMonitoringLogEntry,
  IPcfSyncJob,
} from 'types/custom';
import type { SnackbarActions } from './useSnackbar';

export type UsePCF = (
  fetchData: FetchData,
  snackbar: SnackbarActions,
) => [{ loading: boolean }, IActions];

interface IActions {
  getSyncJobs: () => Promise<IPcfSyncJob[]>;
  getLogs: () => Promise<IPcfMonitoringLogEntry[]>;
  getMissingObjects: () => Promise<IPcfMissingObject[]>;
  getSyncJob: (id: string) => Promise<IPcfSyncJob | undefined>;
  getMissingObject: (id: string) => Promise<IPcfMissingObject | undefined>;
  getMarketAuthEntries: (
    market: string,
    modelYear?: number,
    carType?: string,
    offset?: number,
    limit?: number,
  ) => Promise<IPcfMarketAuthEntriesResponse | undefined>;
  getCarConfigs: (
    market: string,
    modelYear?: number,
    carType?: string,
    offset?: number,
    limit?: number,
  ) => Promise<IPcfCarConfigsResponse | undefined>;
}

export const usePCF: UsePCF = (makeRequest, snackbar) => {
  const { loading, createAction } = useCachedActions(snackbar);

  const actions = useMemo<IActions>(
    () => ({
      getCarConfigs: createAction(
        (market, modelYear, carType, offset, limit) =>
          buildCacheKey('getCarConfigs', {
            market,
            modelYear,
            carType,
            offset,
            limit,
          }),
        (
          market: string,
          modelYear?: number,
          carType?: string,
          offset?: number,
          limit?: number,
        ) =>
          makeRequest<IPcfCarConfigsResponse>(
            '/api/assortment/pcf/cars/car-config',
            {
              market,
              carType,
              limit: toQueryStringValue(limit),
              offset: toQueryStringValue(offset),
              modelYear: toQueryStringValue(modelYear),
            },
          ),
        undefined,
      ),
      getMarketAuthEntries: createAction(
        (market, modelYear, carType, offset, limit) =>
          buildCacheKey('getMarketAuthEntries', {
            market,
            modelYear,
            carType,
            offset,
            limit,
          }),
        (
          market: string,
          modelYear?: number,
          carType?: string,
          offset?: number,
          limit?: number,
        ) =>
          makeRequest<IPcfMarketAuthEntriesResponse>(
            '/api/assortment/pcf/cars/market-auth/entries',
            {
              market,
              carType,
              limit: toQueryStringValue(limit),
              offset: toQueryStringValue(offset),
              modelYear: toQueryStringValue(modelYear),
            },
          ),
        undefined,
      ),
      getSyncJobs: createAction(
        () => 'getSyncJobs',
        () =>
          makeRequest<IPcfSyncJob[]>('/api/assortment/pcf/sync-jobs').then(
            (r) => r ?? [],
          ),
        [],
      ),
      getSyncJob: createAction(
        (id) => buildCacheKey('getSyncJob', { id }),
        (id: string) =>
          makeRequest<IPcfSyncJob>(`/api/assortment/pcf/sync-jobs/${id}`),
        undefined,
      ),
      getMissingObjects: createAction(
        () => 'getMissingObjects',
        () =>
          makeRequest<IPcfMissingObject[]>(
            '/api/assortment/pcf/missing-objects',
          ).then((r) => r ?? []),
        [],
      ),
      getMissingObject: createAction(
        (id) => buildCacheKey('getMissingObject', { id }),
        (id: string) =>
          makeRequest<IPcfMissingObject>(
            `/api/assortment/pcf/missing-objects/${id}`,
          ),
        undefined,
      ),
      getLogs: createAction(
        () => 'getLogs',
        () =>
          makeRequest<IPcfMonitoringLogEntry[]>(
            '/api/assortment/pcf/logs',
          ).then((r) => r ?? []),
        [],
      ),
    }),
    [createAction, makeRequest],
  );

  return [{ loading }, actions];
};
